import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { toast } from "sonner";
import { invalidateSfxCache, playSfxAsset } from "../../lib/soundBus";
import { MUSIC_MOODS, SFX_TAGS } from "../../lib/soundTags";
import CombatTrackerPanel from "./CombatTrackerPanel";
import HomebrewRulesetEditor from "./HomebrewRulesetEditor";
import HomebrewAncestryClassManager from "./HomebrewAncestryClassManager";
import { VariantRulesEditor } from "./VariantRulesEditor";
import GmNpcsTab from "./GmNpcsTab";
import GmStoryTab from "./GmStoryTab";
import GmRewardsTab from "./GmRewardsTab";
import GmSfxTab from "./GmSfxTab";
import GmAssetsTab from "./GmAssetsTab";
import GmItemsTab from "./GmItemsTab";
import GmHelpTab from "./GmHelpTab";
import FreeRoamGmPanel from "./FreeRoamGmPanel";
import { InfoTip } from "./InfoTip";
import { ChevronLeft, ChevronRight, Copy, Upload, Music, Volume2, Play, Pause, LockOpen, Maximize2 } from "lucide-react";

export default function GmSidebar({ game, scene, members, tokens, onGameUpdate, onSceneUpdate, onAddToken, onSpawnToken, collapsed, setCollapsed }) {
  const fileRef = useRef(null);
  const musicFileRef = useRef(null);
  const sfxFileRef = useRef(null);
  const musicPreviewClickRef = useRef(false);
  const isScopedGeneral = !!game.my_branch_id;
  const [sceneName, setSceneName] = useState(scene?.name || "");
  const [gridSizeDraft, setGridSizeDraft] = useState(scene?.grid_size || 50);
  const [areaWidthDraft, setAreaWidthDraft] = useState(Math.round((scene?.width || 1600) / (scene?.grid_size || 50)));
  const [areaHeightDraft, setAreaHeightDraft] = useState(Math.round((scene?.height || 1000) / (scene?.grid_size || 50)));
  const [lightRadiusDraft, setLightRadiusDraft] = useState(20);
  const [lightTypeDraft, setLightTypeDraft] = useState("fire");
  const [lightPickableDraft, setLightPickableDraft] = useState(false);
  const [musicAssets, setMusicAssets] = useState([]);
  const [musicUploadMood, setMusicUploadMood] = useState("explore");
  const [sfxTag, setSfxTag] = useState("attack_hit");

  useEffect(() => setSceneName(scene?.name || ""), [scene?.id]);
  useEffect(() => setGridSizeDraft(scene?.grid_size || 50), [scene?.id, scene?.grid_size]);
  useEffect(() => {
    const gs = scene?.grid_size || 50;
    setAreaWidthDraft(Math.round((scene?.width || 1600) / gs));
    setAreaHeightDraft(Math.round((scene?.height || 1000) / gs));
  }, [scene?.id, scene?.width, scene?.height, scene?.grid_size]);
  useEffect(() => {
    api.get("/uploads", { params: { game_id: game.id, kind: "music" } }).then((r) => setMusicAssets(r.data));
  }, [game.id]);

  const updateGame = async (updates) => {
    const { data } = await api.patch(`/games/${game.id}`, updates);
    onGameUpdate({ ...game, ...data });
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${game.invite_code}`);
    toast.success("Invite link copied");
  };

  const uploadMap = async (e) => {
    const file = e.target.files[0];
    if (!file || !scene) return;
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`/uploads?game_id=${game.id}&kind=map`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    const url = fileUrl(data.id);
    const { data: updatedScene } = await api.patch(`/games/${game.id}/scenes/${scene.id}`, { map_asset_url: url });
    onSceneUpdate(updatedScene);
    toast.success("Map uploaded");
  };

  const saveSceneName = async () => {
    if (!scene) return;
    const { data } = await api.patch(`/games/${game.id}/scenes/${scene.id}`, { name: sceneName });
    onSceneUpdate(data);
  };

  const uploadMusic = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`/uploads?game_id=${game.id}&kind=music&tag=${musicUploadMood}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    setMusicAssets((prev) => [...prev, { ...data, tag: musicUploadMood }]);
    toast.success(`"${musicUploadMood}" music track uploaded`);
  };

  const uploadSfx = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    await api.post(`/uploads?game_id=${game.id}&kind=sfx&tag=${encodeURIComponent(sfxTag)}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    invalidateSfxCache(game.id);
    toast.success(`Generic "${sfxTag}" sound uploaded — plays automatically for anyone without a custom sound`);
  };

  const setMusic = async (assetId) => {
    if (!scene) return;
    const asset = musicAssets.find((a) => a.id === assetId);
    const url = asset ? fileUrl(asset.id) : null;
    const { data } = await api.patch(`/games/${game.id}/scenes/${scene.id}`, { music_asset_url: assetId === "none" ? null : url, mood: asset?.tag || null });
    onSceneUpdate(data);
  };

  const setSceneMood = async (mood) => {
    const match = musicAssets.find((a) => a.tag === mood);
    const url = match ? fileUrl(match.id) : null;
    const { data } = await api.patch(`/games/${game.id}/scenes/${scene.id}`, { mood, music_asset_url: url });
    onSceneUpdate(data);
    if (!match) toast.info(`No music tagged "${mood}" yet — scene mood saved, upload a track for it anytime`);
  };

  const addLightSource = () => {
    if (!scene) return toast.error("Pick a scene first");
    const radius = Math.max(5, parseInt(lightRadiusDraft, 10) || 20);
    onSpawnToken({
      name: lightTypeDraft === "fire" ? "Campfire" : lightTypeDraft === "magic" ? "Arcane Orb" : "Sparking Coil",
      token_type: "light", size: 30, light_radius_ft: radius, light_type: lightTypeDraft, light_pickable: lightPickableDraft, image_url: null,
    });
    toast.success(`Light source added (${radius}ft, ${lightTypeDraft}${lightPickableDraft ? ", pickable" : ""})`);
  };

  const unlockAllDoors = async () => {
    const { data } = await api.post(`/games/${game.id}/doors/unlock-all`);
    toast.success(data.unlocked > 0 ? `Unlocked ${data.unlocked} door${data.unlocked === 1 ? "" : "s"} — the way through is open.` : "No locked doors to unlock.");
  };

  const saveMapArea = () => {
    if (!scene) return;
    const gs = scene.grid_size || 50;
    const squaresW = Math.max(10, Math.min(400, parseInt(areaWidthDraft, 10) || Math.round(scene.width / gs)));
    const squaresH = Math.max(10, Math.min(400, parseInt(areaHeightDraft, 10) || Math.round(scene.height / gs)));
    setAreaWidthDraft(squaresW);
    setAreaHeightDraft(squaresH);
    api.patch(`/games/${game.id}/scenes/${scene.id}`, { width: squaresW * gs, height: squaresH * gs }).then((r) => onSceneUpdate(r.data));
  };

  if (collapsed) {
    return (
      <button
        data-testid="gm-sidebar-expand"
        onClick={() => setCollapsed(false)}
        className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="absolute top-4 left-4 bottom-20 md:bottom-4 z-20 w-80 max-w-[calc(100vw-2rem)] bg-black/60 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-y-auto scrollbar-thin p-4 space-y-4" data-testid="gm-sidebar">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">GM Tools</h3>
        <button data-testid="gm-sidebar-collapse" onClick={() => setCollapsed(true)} className="text-[#f1e0c5]/50 hover:text-[#D4AF37]">
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      <button
        data-testid="gm-pause-game-toggle"
        onClick={() => !isScopedGeneral && updateGame({ paused: !game.paused })}
        disabled={isScopedGeneral}
        title={isScopedGeneral ? "Only the Master GM can pause the shared Raid table" : undefined}
        className={`w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors duration-200 ${
          isScopedGeneral ? "bg-black/10 border-white/5 text-[#f1e0c5]/25 cursor-not-allowed" :
          game.paused ? "bg-[#D90429] border-[#D90429] text-white" : "bg-black/30 border-white/10 text-[#f1e0c5]/70 hover:border-[#D4AF37]/50"
        }`}
      >
        {game.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        {game.paused ? "Game Paused — Tap to Resume" : "Pause Game"}
      </button>

      <div className="bg-black/30 rounded-xl p-3">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Invite Code</p>
        <div className="flex items-center justify-between">
          <span className="font-mono-sheet text-lg text-[#D4AF37]" data-testid="gm-invite-code">{game.invite_code}</span>
          <button data-testid="gm-copy-invite" onClick={copyInvite}><Copy className="w-4 h-4 text-[#f1e0c5]/60 hover:text-[#D4AF37]" /></button>
        </div>
      </div>

      <Tabs defaultValue="scene">
        <TabsList className="flex justify-start overflow-x-auto scrollbar-thin w-full bg-black/30 h-auto gap-0.5 p-0.5">
          <TabsTrigger data-testid="gm-tab-scene" value="scene" className="text-[10px] px-2 shrink-0 whitespace-nowrap">Scene</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-npcs" value="npcs" className="text-[10px] px-2 shrink-0 whitespace-nowrap">NPCs</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-story" value="story" className="text-[10px] px-2 shrink-0 whitespace-nowrap">Story</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-rewards" value="rewards" className="text-[10px] px-2 shrink-0 whitespace-nowrap">Rewards</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-sfx" value="sfx" className="text-[10px] px-2 shrink-0 whitespace-nowrap">SFX</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-assets" value="assets" className="text-[10px] px-2 shrink-0 whitespace-nowrap">Assets</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-items" value="items" className="text-[10px] px-2 shrink-0 whitespace-nowrap">Items</TabsTrigger>
          <TabsTrigger data-testid="gm-tab-help" value="help" className="text-[10px] px-2 shrink-0 whitespace-nowrap">?</TabsTrigger>
        </TabsList>

        <TabsContent value="scene" className="space-y-4 pt-3">
          <div className="space-y-3">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40">Grid Type {isScopedGeneral && <span className="text-[#f1e0c5]/25">(Master GM only)</span>}</p>
            <Select value={game.grid_type} disabled={isScopedGeneral} onValueChange={(v) => updateGame({ grid_type: v })}>
              <SelectTrigger data-testid="gm-grid-type-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="square">Square</SelectItem>
                <SelectItem value="hex">Hex</SelectItem>
                <SelectItem value="gridless">Gridless</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">Grid Square Size</p>
              <p className="text-[10px] text-[#D4AF37]">{gridSizeDraft}px / 5ft</p>
            </div>
            <Slider
              data-testid="gm-grid-size-slider"
              value={[gridSizeDraft]}
              min={20}
              max={150}
              step={5}
              onValueChange={([v]) => setGridSizeDraft(v)}
              onValueCommit={([v]) => scene && api.patch(`/games/${game.id}/scenes/${scene.id}`, { grid_size: v }).then((r) => onSceneUpdate(r.data))}
            />
            <p className="text-[10px] text-[#f1e0c5]/30">
              Smaller squares fit a bigger area on the same map image ({scene ? Math.round((scene.width / gridSizeDraft) * 5) : "—"}ft × {scene ? Math.round((scene.height / gridSizeDraft) * 5) : "—"}ft playable area). Bigger squares zoom in for a smaller, more detailed room.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Maximize2 className="w-3 h-3" /> Map Size (Playable Area)</p>
            <p className="text-[10px] text-[#f1e0c5]/30">More squares = more room for tokens to maneuver around, independent of zoom. Scroll/pinch to pan across a bigger map.</p>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  data-testid="gm-map-width-input"
                  type="number"
                  min={10}
                  max={400}
                  value={areaWidthDraft}
                  onChange={(e) => setAreaWidthDraft(e.target.value)}
                  onBlur={saveMapArea}
                  className="bg-black/30 border-white/10 h-8 text-xs"
                />
                <p className="text-[9px] text-[#f1e0c5]/30 mt-0.5 text-center">Width (squares) — {areaWidthDraft * 5}ft</p>
              </div>
              <span className="text-[#f1e0c5]/30 text-xs">×</span>
              <div className="flex-1">
                <Input
                  data-testid="gm-map-height-input"
                  type="number"
                  min={10}
                  max={400}
                  value={areaHeightDraft}
                  onChange={(e) => setAreaHeightDraft(e.target.value)}
                  onBlur={saveMapArea}
                  className="bg-black/30 border-white/10 h-8 text-xs"
                />
                <p className="text-[9px] text-[#f1e0c5]/30 mt-0.5 text-center">Height (squares) — {areaHeightDraft * 5}ft</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 border-t border-white/10 pt-3">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40">Add a Light Source</p>
            <p className="text-[10px] text-[#f1e0c5]/30">A dedicated glowing token (not an NPC) that just lights up the area — a campfire, a magic lantern, a sconce.</p>
            <div className="flex gap-1.5">
              <Input data-testid="gm-light-radius-input" type="number" min={5} value={lightRadiusDraft} onChange={(e) => setLightRadiusDraft(e.target.value)} placeholder="ft radius" className="bg-black/30 border-white/10 h-8 text-xs w-24" />
              <Select value={lightTypeDraft} onValueChange={setLightTypeDraft}>
                <SelectTrigger data-testid="gm-light-type-select" className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fire">Fire</SelectItem>
                  <SelectItem value="magic">Magic</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="gm-add-light-button" size="sm" variant="secondary" className="rounded-full shrink-0" onClick={addLightSource}>Add</Button>
            </div>
            <label className="flex items-center gap-1.5 pt-1 cursor-pointer">
              <Checkbox data-testid="gm-light-pickable-checkbox" checked={lightPickableDraft} onCheckedChange={(v) => setLightPickableDraft(!!v)} />
              <span className="text-[10px] text-[#f1e0c5]/50">Players can pick this up and attach it to their token</span>
            </label>
          </div>

          <div className="border-t border-white/10 pt-3">
            <button
              data-testid="gm-unlock-all-doors-button"
              onClick={unlockAllDoors}
              title="Unlocks every locked (unbroken) door you can see — great for letting a Waiting Room through onto the next map all at once"
              className="w-full flex items-center justify-center gap-2 rounded-full py-2 text-xs font-bold uppercase tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-200"
            >
              <LockOpen className="w-3.5 h-3.5" /> Unlock All Doors
            </button>
          </div>

          {isScopedGeneral ? (
            <p className="text-[10px] text-[#f1e0c5]/25 border-t border-white/10 pt-3">Token approval, Fog of War, Dynamic Lighting &amp; Speed Restrictions are table-wide — only the Master GM can change them.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#f1e0c5]/70">Require token approval</p>
                <Switch data-testid="gm-token-approval-switch" checked={game.token_approval_required} onCheckedChange={(v) => updateGame({ token_approval_required: v })} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#f1e0c5]/70 flex items-center gap-1.5">Fog of War <InfoTip topic="vision_lighting" /></p>
                <Switch data-testid="gm-fog-switch" checked={game.fog_of_war_enabled} onCheckedChange={(v) => updateGame({ fog_of_war_enabled: v })} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#f1e0c5]/70">Dynamic Lighting</p>
                <Switch data-testid="gm-lighting-switch" checked={game.dynamic_lighting_enabled} onCheckedChange={(v) => updateGame({ dynamic_lighting_enabled: v })} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#f1e0c5]/70">Speed Restrictions</p>
                <Switch data-testid="gm-speed-restrictions-switch" checked={game.speed_restrictions_enabled} onCheckedChange={(v) => updateGame({ speed_restrictions_enabled: v })} />
              </div>
            </>
          )}

          <FreeRoamGmPanel game={game} tokens={tokens} />

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5">Scene <InfoTip topic="map_creation" /></p>
            <Input data-testid="gm-scene-name-input" value={sceneName} onChange={(e) => setSceneName(e.target.value)} onBlur={saveSceneName} className="bg-black/30 border-white/10" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadMap} data-testid="gm-map-upload-input" />
            <Button variant="secondary" size="sm" className="w-full rounded-full gap-2" onClick={() => fileRef.current?.click()} data-testid="gm-upload-map-button">
              <Upload className="w-3.5 h-3.5" /> Upload Map Image
            </Button>
            <Select value={scene?.weather || "none"} onValueChange={(v) => onSceneUpdate ? api.patch(`/games/${game.id}/scenes/${scene.id}`, { weather: v }).then((r) => onSceneUpdate(r.data)) : null}>
              <SelectTrigger data-testid="gm-weather-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Weather" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Weather</SelectItem>
                <SelectItem value="rain">Rain</SelectItem>
                <SelectItem value="snow">Snow</SelectItem>
                <SelectItem value="fog">Misty Fog</SelectItem>
              </SelectContent>
            </Select>
            <Select value={scene?.darkness_mode || "none"} onValueChange={(v) => onSceneUpdate ? api.patch(`/games/${game.id}/scenes/${scene.id}`, { darkness_mode: v }).then((r) => onSceneUpdate(r.data)) : null}>
              <SelectTrigger data-testid="gm-darkness-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Darkness" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Darkness</SelectItem>
                <SelectItem value="nonmagical">Non-Magical Darkness</SelectItem>
                <SelectItem value="magical">Magical Darkness</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#f1e0c5]/30">Non-magical: darkvision/low-light see through it. Magical: blinds everyone except tokens set to "See in Darkness".</p>
            <div className="flex items-center justify-between pt-1">
              <div>
                <p className="text-xs text-[#f1e0c5]/70">Full Sight (this map)</p>
                <p className="text-[10px] text-[#f1e0c5]/30">Players see everything on this map exactly like the GM — bypasses fog/vision/darkness.</p>
              </div>
              <Switch data-testid="gm-full-sight-switch" checked={!!scene?.full_sight} onCheckedChange={(v) => onSceneUpdate ? api.patch(`/games/${game.id}/scenes/${scene.id}`, { full_sight: v }).then((r) => onSceneUpdate(r.data)) : null} />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Music className="w-3 h-3" /> Ambient Music</p>
            <Select value={scene?.mood || "explore"} onValueChange={setSceneMood}>
              <SelectTrigger data-testid="gm-scene-mood-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Scene mood" /></SelectTrigger>
              <SelectContent>{MUSIC_MOODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-[#f1e0c5]/30">Auto-picks a track tagged with this mood. Or choose manually:</p>
            <Select value="pick" onValueChange={setMusic}>
              <SelectTrigger data-testid="gm-music-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Choose track manually" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {musicAssets.map((a) => (
                  <SelectItem
                    key={a.id}
                    value={a.id}
                    onSelect={(e) => {
                      if (musicPreviewClickRef.current) {
                        e.preventDefault();
                        musicPreviewClickRef.current = false;
                      }
                    }}
                  >
                    <span className="flex items-center gap-2 pr-2">
                      <button
                        type="button"
                        data-testid={`music-preview-${a.id}`}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          musicPreviewClickRef.current = true;
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          playSfxAsset(a.id);
                        }}
                        className="text-[#D4AF37] hover:text-white shrink-0"
                      >
                        <Play className="w-3 h-3" />
                      </button>
                      {a.original_filename} ({a.tag})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Select value={musicUploadMood} onValueChange={setMusicUploadMood}>
                <SelectTrigger data-testid="gm-music-upload-mood-select" className="bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{MUSIC_MOODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              <input ref={musicFileRef} type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="gm-music-upload-input" />
              <Button variant="secondary" size="sm" className="rounded-full gap-2 shrink-0" onClick={() => musicFileRef.current?.click()} data-testid="gm-upload-music-button">
                <Upload className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Volume2 className="w-3.5 h-3.5 text-[#f1e0c5]/40" />
              <input
                data-testid="gm-music-volume-slider"
                type="range" min={0} max={1} step={0.05} value={game.music_volume ?? 0.6}
                disabled={isScopedGeneral}
                onChange={(e) => !isScopedGeneral && updateGame({ music_volume: parseFloat(e.target.value) })}
                className="flex-1"
              />
            </div>
            {isScopedGeneral && <p className="text-[10px] text-[#f1e0c5]/25">Music volume is table-wide — only the Master GM can change it.</p>}
          </div>

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40">Generic Sound Effects</p>
            <p className="text-[10px] text-[#f1e0c5]/30">Upload once per category — plays automatically for any player who hasn't set their own weapon/spell sound.</p>
            <div className="flex gap-2">
              <Select value={sfxTag} onValueChange={setSfxTag}>
                <SelectTrigger data-testid="gm-sfx-tag-select" className="bg-black/30 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SFX_TAGS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <input ref={sfxFileRef} type="file" accept="audio/*" className="hidden" onChange={uploadSfx} />
              <Button data-testid="gm-upload-sfx-button" size="sm" variant="secondary" className="rounded-full shrink-0" onClick={() => sfxFileRef.current?.click()}>
                <Upload className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="npcs" className="pt-3">
          <GmNpcsTab game={game} scene={scene} tokens={tokens} onAddToken={onAddToken} onSpawnToken={onSpawnToken} />
        </TabsContent>

        <TabsContent value="story" className="pt-3">
          <GmStoryTab game={game} onGameUpdate={onGameUpdate} onSpawnToken={onSpawnToken} />
        </TabsContent>

        <TabsContent value="rewards" className="pt-3">
          <GmRewardsTab game={game} scene={scene} tokens={tokens} onSpawnToken={onSpawnToken} />
        </TabsContent>

        <TabsContent value="sfx" className="pt-3">
          <GmSfxTab game={game} />
        </TabsContent>

        <TabsContent value="assets" className="pt-3">
          <GmAssetsTab game={game} scene={scene} onSpawnToken={onSpawnToken} />
        </TabsContent>

        <TabsContent value="items" className="pt-3">
          <GmItemsTab game={game} scene={scene} tokens={tokens} members={members} />
        </TabsContent>

        <TabsContent value="help" className="pt-3">
          <GmHelpTab />
        </TabsContent>
      </Tabs>

      {game.system === "homebrew" && (
        <div className="border-t border-white/10 pt-4">
          <p className="font-display text-lg mb-2">Homebrew Ruleset</p>
          <HomebrewRulesetEditor gameId={game.id} ruleset={game.homebrew_ruleset} onSave={(data) => onGameUpdate({ ...game, ...data })} />
        </div>
      )}

      {game.system === "homebrew" && (
        <div className="border-t border-white/10 pt-4">
          <p className="font-display text-lg mb-2">Custom Ancestries &amp; Classes</p>
          <HomebrewAncestryClassManager gameId={game.id} />
        </div>
      )}

      {(game.system === "pf2e" || game.system === "dnd5e") && !isScopedGeneral && (
        <div className="border-t border-white/10 pt-4">
          <p className="font-display text-lg mb-2">Variant Rules</p>
          <VariantRulesEditor
            system={game.system}
            value={game.variant_rules || {}}
            onChange={async (next) => {
              const { data } = await api.patch(`/games/${game.id}`, { variant_rules: next });
              onGameUpdate({ ...game, ...data });
            }}
          />
        </div>
      )}

      <div className="border-t border-white/10 pt-4">
        <CombatTrackerPanel gameId={game.id} />
      </div>

      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Members ({members.length})</p>
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center justify-between text-sm bg-black/20 rounded-lg px-2 py-1.5">
              <span>{m.display_name}</span>
              <span className="text-[10px] uppercase text-[#D4AF37]/70">{m.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
